import React, { useState } from "react";
import { gql } from "@apollo/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@src/lib/ui/Dialog";
import { Button } from "@src/lib/ui/Button";
import Text from "@src/lib/ui/Text";
import { cn } from "@src/lib/utils/cn";
import PlusIcon from "@src/lib/icons/18px/plus";
import CheckmarkIcon from "@src/lib/icons/18px/checkmark";
import { BotOrb, defaultAgentColor, useHoverSpin } from "./AgentAvatar";
import AgentOrbitHeader from "./AgentOrbitHeader";
import { defaultBotShape } from "./botOrb";
import { useUpdateOpusAgentMutation } from "./AgentDialog.generated";
import { OpusAgentAvatarKind } from "@src/types.generated";
import {
  OpusAgentFieldsFragment,
  useOpusAgentsQuery,
} from "@src/features/opus-agent/agentPanel.generated";
import {
  OpusAgentTemplatesQuery,
  useCreateOpusAgentFromTemplateMutation,
  useOpusAgentTemplatesQuery,
} from "./AgentGalleryDialog.generated";

export type Template = OpusAgentTemplatesQuery["OpusAgentTemplates"][number];

export const groupTemplates = (
  templates: Template[],
): { category: Template["category"]; templates: Template[] }[] => {
  const groups = new Map<
    string,
    { category: Template["category"]; templates: Template[] }
  >();
  for (const template of templates) {
    const existing = groups.get(template.category.id);
    if (existing) {
      existing.templates.push(template);
    } else {
      groups.set(template.category.id, {
        category: template.category,
        templates: [template],
      });
    }
  }
  return [...groups.values()].sort(
    (left, right) => left.category.index - right.category.index,
  );
};

export const useAddAgentFromTemplate = (
  onCreated: (agent: OpusAgentFieldsFragment) => void,
): {
  addingId: string | null;
  error: string | null;
  add: (templateId: string) => Promise<void>;
} => {
  const [createFromTemplate] = useCreateOpusAgentFromTemplateMutation({
    refetchQueries: ["OpusAgents"],
  });
  const [updateAgent] = useUpdateOpusAgentMutation();
  const [addingId, setAddingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const add = async (templateId: string) => {
    setError(null);
    setAddingId(templateId);
    try {
      const result = await createFromTemplate({ variables: { templateId } });
      const agent = result.data?.createOpusAgentFromTemplate?.agent;
      if (agent) {
        onCreated(agent);
        // Keep the bot its card showed. Best effort: without it the agent
        // shows the default for its own id.
        void updateAgent({
          variables: {
            id: agent.id,
            input: {
              name: agent.name,
              emoji: agent.emoji,
              instructions: agent.instructions,
              avatar: {
                kind: OpusAgentAvatarKind.Bot,
                botShape: templateBotShape(templateId),
                botColor: "auto",
              },
            },
          },
        }).catch(() => undefined);
      } else {
        setError("Couldn't add this agent. Please try again.");
      }
    } catch {
      setError("Couldn't add this agent. Please try again.");
    } finally {
      setAddingId(null);
    }
  };

  return { addingId, error, add };
};

// Each template's bot, stable per template: an agent added from it keeps the
// bot its card showed.
const templateBotShape = defaultBotShape;

// A gallery card: tile, name, the full description, and Add (or Added once
// the org has an active agent made from it).
export const TemplateRow: React.FC<{
  template: Template;
  added: boolean;
  addingId: string | null;
  onAdd: (id: string) => void;
  // In place of the bot tile (the design page's glyph comparison).
  avatar?: React.ReactNode;
}> = ({ template, added, addingId, onAdd, avatar }) => {
  // Hovering (or focusing) anywhere on the card greets with its bot.
  const greet = useHoverSpin();
  return (
    <div
      className="flex min-w-0 items-start gap-3 rounded-xl border border-border-primary p-3 transition-colors hover:bg-state-hover"
      data-testid={`agent-gallery-card-${template.id}`}
      onMouseEnter={greet.onEnter}
      onMouseLeave={greet.onLeave}
      onFocus={greet.onEnter}
      onBlur={greet.onLeave}
    >
      {/* The bot an agent made from this template starts with. */}
      {avatar ?? (
        <span className="flex size-10 shrink-0 items-center justify-center rounded-lg border border-border-primary bg-bg-primary">
          <BotOrb
            color={defaultAgentColor(template.id)}
            size={30}
            state={greet.state}
          />
        </span>
      )}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5 pt-0.5">
        <Text
          variant="compact-title"
          className="font-semibold-v4 text-text-primary"
          ellipsis
        >
          {template.name}
        </Text>
        <Text
          variant="helper-description"
          // Instructions stand in for a missing description; they run long.
          className={cn(
            "text-text-secondary",
            !template.description && "line-clamp-3",
          )}
          multiline
        >
          {template.description || template.instructions}
        </Text>
      </div>
      {added ? (
        <span
          className="flex h-7 shrink-0 items-center gap-1 px-1 text-text-secondary"
          data-testid={`agent-gallery-added-${template.id}`}
        >
          <CheckmarkIcon className="size-3.5 text-icon-secondary" />
          <Text variant="helper-description" className="text-text-secondary">
            Added
          </Text>
        </span>
      ) : (
        <Button
          variant="secondary"
          size="sm"
          className="shrink-0 px-3"
          loading={addingId === template.id}
          disabled={addingId !== null}
          onClick={() => onAdd(template.id)}
          data-testid={`agent-gallery-add-${template.id}`}
        >
          Add
        </Button>
      )}
    </div>
  );
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (agent: OpusAgentFieldsFragment) => void;
  onCreateFromScratch: () => void;
};

const AgentGalleryDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  onCreated,
  onCreateFromScratch,
}) => {
  const { data, loading } = useOpusAgentTemplatesQuery();
  const { data: agentsData } = useOpusAgentsQuery();
  const { addingId, error, add } = useAddAgentFromTemplate((agent) => {
    onCreated(agent);
    onOpenChange(false);
  });
  const templates = data?.OpusAgentTemplates ?? [];
  const groups = groupTemplates(templates);
  const addedTemplateIds = new Set(
    (agentsData?.OpusAgents ?? [])
      .filter((agent) => !agent.archivedAt && agent.template)
      .map((agent) => agent.template?.id),
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex h-[80vh] max-w-3xl flex-col gap-0 overflow-hidden rounded-2xl p-0 outline-none sm:rounded-2xl"
        hideDefaultTitle
        // Focus the dialog itself: its first control is Create from
        // scratch, which would otherwise open ringed as the default action.
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          (event.currentTarget as HTMLElement | null)?.focus();
        }}
        data-testid="agent-gallery-dialog"
      >
        <AgentOrbitHeader>
          <DialogHeader className="flex-row items-end justify-between gap-3 space-y-0">
            <div className="flex min-w-0 flex-col gap-0.5">
              <DialogTitle className="text-l-v4">Agent gallery</DialogTitle>
              <Text
                variant="compact-description"
                className="text-text-secondary"
                multiline
              >
                Add a starting point from Opus, then edit its instructions to
                fit your org.
              </Text>
            </div>
            <Button
              variant="secondary"
              size="sm"
              className="shrink-0 bg-bg-primary"
              onClick={onCreateFromScratch}
              data-testid="agent-gallery-scratch"
            >
              <PlusIcon />
              Create from scratch
            </Button>
          </DialogHeader>
        </AgentOrbitHeader>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 pt-1 pb-6">
          {loading ? (
            <Text
              variant="compact-description"
              className="block px-0.5 pt-3 text-text-tertiary"
            >
              Loading…
            </Text>
          ) : templates.length === 0 ? (
            <Text
              variant="compact-description"
              className="block px-0.5 pt-3 text-text-tertiary"
            >
              No gallery agents yet
            </Text>
          ) : (
            <div className="flex flex-col gap-2">
              {groups.map((group) => (
                <section
                  key={group.category.id}
                  className="flex flex-col gap-2 pt-3"
                  data-testid={`agent-gallery-section-${group.category.id}`}
                >
                  <Text
                    variant="compact-title"
                    className="px-0.5 text-text-primary"
                  >
                    {group.category.name}
                  </Text>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {group.templates.map((template) => (
                      <TemplateRow
                        key={template.id}
                        template={template}
                        added={addedTemplateIds.has(template.id)}
                        addingId={addingId}
                        onAdd={add}
                      />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
          {error && (
            <Text
              variant="compact-description"
              className="mt-3 block px-0.5 text-status-error-text"
            >
              {error}
            </Text>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AgentGalleryDialog;

gql`
  query OpusAgentTemplates {
    OpusAgentTemplates {
      id
      key
      name
      emoji
      description
      instructions
      category {
        id
        name
        index
      }
    }
  }

  mutation CreateOpusAgentFromTemplate($templateId: UUID!) {
    createOpusAgentFromTemplate(templateId: $templateId) {
      success
      error {
        code
        message
      }
      agent {
        ...OpusAgentFields
      }
    }
  }
`;
